import './App.css'
import { Toaster } from "@/components/ui/toaster"
import { Toaster as SonnerToaster } from "sonner"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import VisualEditAgent from '@/lib/VisualEditAgent'
import NavigationTracker from '@/lib/NavigationTracker'
import PageDebugger from '@/lib/PageDebugger'
import { pagesConfig } from './pages.config'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import AuthCallback from '@/pages/AuthCallback';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;

const LayoutWrapper = ({ children, currentPageName }) => Layout ?
  <Layout currentPageName={currentPageName}>{children}</Layout>
  : <>{children}</>;

const AuthenticatedApp = () => {
  const { isLoadingAuth, authError, isAuthenticated } = useAuth();
  const LandingPage = Pages['Landing'];

  // Show loading spinner while checking auth
  if (isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'access_denied') {
      // Show access denied message on landing page
      return <Pages.Landing />;
    }
  }

  // Render the main app
  return (
    <Routes>
      {/* Landing page - no layout wrapper, no auth required */}
      <Route 
        path="/" 
        element={isAuthenticated ? <LayoutWrapper currentPageName="Dashboard"><Pages.Dashboard /></LayoutWrapper> : <LandingPage />} 
      />
      
      {/* Landing route explicitly */}
      <Route path="/Landing" element={<LandingPage />} />
      
      {/* Reset password - no auth required */}
      <Route path="/ResetPassword" element={<Pages.ResetPassword />} />

      {/* Google OAuth callback - no auth required */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Job detail page with dynamic route */}
      <Route
        path="/Jobs/:id"
        element={
          isAuthenticated ? (
            <LayoutWrapper currentPageName="Jobs">
              <Pages.JobDetail />
            </LayoutWrapper>
          ) : (
            <LandingPage />
          )
        }
      />

      {/* All other pages require authentication - redirect to landing if not authenticated */}
      {Object.entries(Pages).map(([path, Page]) => {
        if (path === 'Landing' || path === 'Home' || path === 'JobDetail' || path === 'ResetPassword') return null; // Skip these as they're handled above
        
        return (
          <Route
            key={path}
            path={`/${path}`}
            element={
              isAuthenticated ? (
                <LayoutWrapper currentPageName={path}>
                  <Page />
                </LayoutWrapper>
              ) : (
                <LandingPage />
              )
            }
          />
        );
      })}
      
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};


function App() {

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <NavigationTracker />
          <PageDebugger />
          <AuthenticatedApp />
        </Router>
        <Toaster />
        <SonnerToaster position="top-center" />
        <VisualEditAgent />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App
